import { TDropdownItem } from '@payaca/ambrosia';
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type JSX,
} from 'react';
import { useDispatch } from 'react-redux';

import {
  getCustomerByDealId,
  getRegion,
  getUserRoles,
} from '@/utils/stateAccessors';
import './PreviewAndSendProtoInvoiceControl.sass';

import { useSelector } from '@/api/state';

import { MOMENT_DATE_SHORT_READABLE_FORMAT } from '@payaca/constants/momentFormatConstants';
import { getJobContactFromCustomer } from '@payaca/helpers/customerHelper';
import { buildEmailSubjectPrefixForProjectFromProject } from '@payaca/helpers/dealHelper';
import { currencyPrice } from '@payaca/helpers/financeHelper';
import { InvoicesPermissions } from '@payaca/permissions/invoices/invoices.permissions';
import { userHasRequiredPermission } from '@payaca/permissions/permissions.utils';
import * as accountActions from '@payaca/store/account/accountActions';
import {
  useDeal,
  useJobContent,
  useProposal,
} from '@payaca/store/hooks/appState';
import { getUpcomingReference } from '@payaca/store/invoices/invoicesActions';
import { sendProtoInvoice } from '@payaca/store/proposals/proposalsActions';
import { Job } from '@payaca/types/jobTypesV2';
import moment from 'moment-timezone';
import { useHistory } from 'react-router';
import MarkProtoInvoiceAsSentModal from '../markProtoInvoiceAsSentModal/MarkProtoInvoiceAsSentModal';
import PreviewAndSendSendableDocument from '../previewAndSendSendableDocument/PreviewAndSendSendableDocument';
import ProtoInvoiceDocument from '../protoInvoiceDocument/ProtoInvoiceDocument';

type Props = {
  protoInvoiceId: number;
};

const PreviewAndSendProtoInvoiceControl: FunctionComponent<Props> = ({
  protoInvoiceId,
}: Props): JSX.Element | null => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [showMarkAsSentModal, setShowMarkAsSentModal] = useState(false);
  const [placeholderReference, setPlaceholderReference] = useState<number>();

  const userRoles = useSelector(getUserRoles);

  const companyName = useSelector(
    (state: any) => state.users.myProfile.accounts[0].companyName
  );

  const emailTemplates = useSelector((state) => state.account.emailTemplates);

  const emailTemplate = useMemo(() => {
    return emailTemplates?.sendInvoice || '';
  }, [emailTemplates]);

  const protoInvoice: Job | undefined = useProposal(protoInvoiceId);

  const jobContent = useJobContent(protoInvoice?.jobContentId);

  const deal = useDeal(protoInvoice?.dealId);

  const region = useSelector(getRegion);

  const customer = useSelector((state) => {
    if (!protoInvoice) return;
    return getCustomerByDealId(state, protoInvoice.dealId);
  });

  const customerContact = useMemo(() => {
    if (!customer) return;
    return getJobContactFromCustomer(customer, protoInvoice?.contactId || null);
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'customer'
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: customer.contacts
  }, [customer?.contacts, protoInvoice?.contactId]);

  const currentAccount = useSelector(
    (state: any) => state.users?.myProfile?.accounts[0]
  );

  useEffect(() => {
    dispatch(accountActions.requestGetEmailTemplates(currentAccount.id, true));
    dispatch(
      getUpcomingReference.request({
        callback: setPlaceholderReference,
      })
    );

    return () => {
      dispatch(accountActions.clearEmailTemplates());
    };
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'dispatch', and 'currentAccount.id'
  }, []);

  const navigateToDeal = useCallback(() => {
    history.push(`/projects/${deal?.publicId}/invoices-and-payments`);
  }, [history, deal]);

  const handleSendProtoInvoice = useCallback(
    (
      payload: {
        sendMeACopy: boolean;
        emailCopy: {
          preButton: string;
          postButton: string;
        };
      },
      callback: () => void,
      onErrorCallback: (errorMessages: string[]) => void
    ) => {
      dispatch(
        sendProtoInvoice.request({
          protoInvoiceId: protoInvoiceId,
          data: {
            isMarkAsSent: false,
            sendMeACopy: payload.sendMeACopy,
            emailCopy: payload.emailCopy,
          },
          callback: (invoiceId: number) => {
            callback();
            navigateToDeal();
          },
          onErrorCallback: (errorMessages: string[]) => {
            onErrorCallback(errorMessages);
          },
        })
      );
    },
    [protoInvoiceId, dispatch, navigateToDeal]
  );

  const dueDate = useMemo(() => {
    if (!protoInvoice) return;
    return moment()
      .add(protoInvoice.invoiceConfig?.dueInDays, 'days')
      .format(MOMENT_DATE_SHORT_READABLE_FORMAT);
  }, [protoInvoice]);

  const emailConfig = useMemo(() => {
    if (!protoInvoice) return;
    return {
      subject: `${
        deal && buildEmailSubjectPrefixForProjectFromProject(deal)
      }Your invoice from ${companyName}`,
      bodyTemplate: emailTemplate,
      substitutions: [
        {
          find: 'reference',
          replaceWith:
            protoInvoice.customReference || String(protoInvoice.reference),
        },
        { find: 'company_name', replaceWith: companyName },
        { find: 'customer_name', replaceWith: customerContact?.name },
        {
          find: 'customer_first_name',
          replaceWith: customerContact?.name?.split(' ')[0] || '',
        },
        {
          find: 'amount_due',
          replaceWith: currencyPrice(jobContent?.total || 0, region),
        },
        {
          find: 'due_date',
          replaceWith: dueDate,
        },
      ],
    };
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'deal'
  }, [
    companyName,
    emailTemplate,
    protoInvoice,
    customerContact?.name,
    jobContent?.total,
    region,
    dueDate,
  ]);

  const availableContextMenuItems = useMemo(() => {
    const actions: TDropdownItem[] = [];

    if (
      userHasRequiredPermission(userRoles, [InvoicesPermissions.SEND_INVOICE])
    ) {
      actions.push({
        label: 'Mark as sent',
        onClick: () => setShowMarkAsSentModal(true),
      });
    }

    return actions;
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'userRoles'
  }, []);

  if (!customer || !protoInvoice || !emailConfig) return null;

  return (
    <>
      <PreviewAndSendSendableDocument
        documentType="invoice"
        title={`Invoice ${
          protoInvoice?.customReference
            ? `#${protoInvoice?.customReference}`
            : ''
        }`}
        documentPreview={
          <ProtoInvoiceDocument
            protoInvoiceId={protoInvoiceId}
            placeholderReference={placeholderReference}
          />
        }
        recipientConfig={{
          customer: customer,
          contactId: protoInvoice?.contactId || undefined,
        }}
        emailConfig={emailConfig}
        onSend={handleSendProtoInvoice}
        actions={availableContextMenuItems}
      />
      <MarkProtoInvoiceAsSentModal
        protoInvoiceId={protoInvoiceId}
        isOpen={showMarkAsSentModal}
        onClose={() => setShowMarkAsSentModal(false)}
        markAsSentCallback={navigateToDeal}
      />
    </>
  );
};

export default PreviewAndSendProtoInvoiceControl;
